/** @fileoverview Loads the bounded, filtered options of choice inputs. */

import { useEffect, useMemo, useState } from 'react';

import type {
  FabrikInputComponent,
  FabrikRecordKeyRefValue,
  FabrikInputValue,
  FabrikOptionItem,
  FabrikRowValues,
  InputSnapshot,
} from '../../types/apps';
import { toODataRows } from '../../services/fabrikApi';
import { stableStringify } from '../../lib/utils';
import { resolveEffectiveDates } from './effectiveDates';
import { resolveODataRowFilters } from './odataRowFilters';
import { valueForReference, valueForColumn } from './valueRefs';
import {
  useDataReader,
  useDataVersion,
  useFabrikGlobals,
  useFieldOptionsReader,
} from './appRuntimeContext';
import { uniqueByValue } from './fieldOptions';
import type { FieldValues } from './types';

/**
 * How a choice input gets its options: a loaded list it can filter in the browser, a search on
 * the server page by page, or no options so the viewer types the code.
 */
export type InputOptionsKind = 'list' | 'search' | 'text';
type InputOptionsState = {
  options: FabrikOptionItem[];
  loading: boolean;
  error: string;
  kind: InputOptionsKind;
};
const EMPTY: InputOptionsState = { options: [], loading: false, error: '', kind: 'list' };

const snapshotValue = (
  inputs: InputSnapshot,
  field: string,
  formId?: string
): FabrikInputValue | undefined =>
  formId ? inputs.forms?.[formId]?.[field] : inputs.standalone?.[field];

const readFilterValue = (
  ref: FabrikRecordKeyRefValue,
  fields: FieldValues,
  inputs: InputSnapshot,
  globals: ReturnType<typeof useFabrikGlobals>
): unknown =>
  ref.scope === 'inputs'
    ? snapshotValue(inputs, ref.field, ref.formId)
    : valueForReference(fields, ref, globals);

const isScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

/**
 * Static options stay local; OData options use the same data reader as lists and are cancelled
 * on dependency changes. A system field's answer says whether its options are a list, a search
 * (the dropdown searches them itself), or none (the viewer types the code).
 * @param input the choice input
 * @param fields the record values on screen, for row filters
 * @param inputs the values of the other inputs, for row filters
 */
export function useInputOptions(
  input: FabrikInputComponent,
  fields: FieldValues,
  inputs: InputSnapshot
): InputOptionsState {
  const readData = useDataReader();
  const readFieldOptions = useFieldOptionsReader();
  const dataVersion = useDataVersion();
  const globals = useFabrikGlobals();
  const options = input.options;
  const staticItems = options && 'items' in options ? options.items : undefined;
  const staticOptions = useMemo(() => staticItems && uniqueByValue(staticItems), [staticItems]);
  /*
   * Only the values the row filters and effective dates read decide the options, so picking a
   * value in this or another input does not load them again. A write reloads them, like the lists.
   */
  const filterValues =
    options && 'odata' in options
      ? (options.odata.rowFilters ?? []).flatMap(({ value, when }) => [
          readFilterValue(value, fields, inputs, globals),
          ...(when ? [readFilterValue(when.value, fields, inputs, globals)] : []),
        ])
      : [];
  const effectiveDates =
    options && 'odata' in options
      ? resolveEffectiveDates(options.odata.effectiveDates, (ref) =>
          readFilterValue(ref, fields, inputs, globals)
        )
      : undefined;
  const dependenciesKey = stableStringify({ options, filterValues, effectiveDates, dataVersion });
  const [state, setState] = useState<InputOptionsState>(EMPTY);

  useEffect(() => {
    if (staticOptions) {
      setState({ options: staticOptions, loading: false, error: '', kind: 'list' });
      return;
    }
    if (options && 'systemField' in options) {
      /* The values of a system field, read once per page with their labels. */
      const { systemId, entity, field } = options.systemField;
      let isCurrent = true;
      setState((previous) => ({ ...previous, loading: true, error: '' }));
      readFieldOptions({ systemId, entity, fields: [field] })
        .then((answer) => {
          if (!isCurrent) return;
          const fieldAnswer = answer[field];
          if (fieldAnswer && fieldAnswer.kind !== 'list') {
            setState({ options: [], loading: false, error: '', kind: fieldAnswer.kind });
            return;
          }
          const loaded = (fieldAnswer?.options ?? []).map(({ label, value, optionId }) => ({
            label,
            value,
            ...(optionId ? { optionId } : {}),
          }));
          setState({ options: uniqueByValue(loaded), loading: false, error: '', kind: 'list' });
        })
        .catch((reason: unknown) => {
          if (!isCurrent) return;
          const message = reason instanceof Error ? reason.message : 'Unable to load options.';
          setState({ options: [], loading: false, error: message, kind: 'list' });
        });
      return () => {
        isCurrent = false;
      };
    }
    if (!options || !('odata' in options)) {
      setState(EMPTY);
      return;
    }

    const { odata, label, value } = options;
    let filter: string | undefined;
    try {
      const predicates = resolveODataRowFilters(odata.rowFilters, fields, globals, inputs);
      filter =
        [odata.filter, ...predicates]
          .filter(Boolean)
          .map((part) => `(${part})`)
          .join(' and ') || undefined;
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Unable to load options.';
      setState(
        message.startsWith('Waiting for a dependent') || message.startsWith('Required row data')
          ? EMPTY
          : { options: [], loading: false, error: message, kind: 'list' }
      );
      return;
    }
    const source = {
      type: 'odata' as const,
      id: odata.id,
      systemId: odata.systemId,
      entity: odata.entity,
      values: odata.fields,
      expand: odata.expand,
      filter,
      orderBy: odata.orderBy,
      effectiveDates,
    };
    const controller = new AbortController();
    /* The previous options stay while new ones load, so a picked value keeps its label. */
    setState((previous) => ({ ...previous, loading: true, error: '' }));
    readData({
      dataSource: source,
      options: { top: odata.limit ?? 100, skip: 0 },
      signal: controller.signal,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        const rows = toODataRows(result);
        const mapped = rows.flatMap((row) => {
          const rowValues: FabrikRowValues = { [odata.id]: row };
          const labelValue = valueForColumn(rowValues, label);
          const optionValue = valueForColumn(rowValues, value);
          return typeof labelValue === 'string' && isScalar(optionValue)
            ? [{ label: labelValue, value: optionValue }]
            : [];
        });
        setState({ options: uniqueByValue(mapped), loading: false, error: '', kind: 'list' });
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        const message = reason instanceof Error ? reason.message : 'Unable to load options.';
        if (message.startsWith('Waiting for a dependent')) {
          setState(EMPTY);
        } else {
          setState({ options: [], loading: false, error: message, kind: 'list' });
        }
      });
    return () => controller.abort();
    // dependenciesKey holds the options definition and every value its row filters read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependenciesKey, readData, readFieldOptions, staticOptions]);

  return staticOptions
    ? { options: staticOptions, loading: false, error: '', kind: 'list' }
    : state;
}

export const readInitialInputValue = (
  input: FabrikInputComponent,
  fields: FieldValues,
  inputs: InputSnapshot,
  globals: ReturnType<typeof useFabrikGlobals>,
  variables: { id: string; value: string }[]
): FabrikInputValue | undefined => {
  const initial = input.initialValue;
  if (initial === undefined) return undefined;
  if (!Array.isArray(initial) && typeof initial === 'object') {
    if (initial.scope === 'inputs') return snapshotValue(inputs, initial.field, initial.formId);
    if (initial.scope === 'variables')
      return variables.find(({ id }) => id === initial.field)?.value;
    if (initial.scope === 'globals') return globals[initial.field];
    /* A record field the system left empty starts the input empty, not as the text "null". */
    if (initial.scope === 'record')
      return (valueForReference(fields, initial, globals) ?? undefined) as
        | FabrikInputValue
        | undefined;
    return undefined;
  }
  return initial as FabrikInputValue;
};
